<template>
  <button
    type="submit"
    @click="click"
    :class="`btn ${getInnerStyle} ${getExtraInnerStyle}`"
    :id="identification"
    :name="inputName"
    :value="inputPlaceholder"
    :disabled="dissabled">

    <span class="inner-content-container">
      <!-- Left icon -->
      <span
        v-if="iconLeft !== null"
        :class="{
          'btn-icon': true,
          'left': true,
          'fa-icon-offset': faIcon
        }">

        <icon
          v-if="faIcon"
          v-bind:style="{color: iconColor}" :name="iconLeft" />

        <q-icon
          v-else
          v-bind:style="{color: iconColor}"
          :height="iconHeight"
          :name="iconLeft" />

      </span>

      <!-- Complex buttons label -->
      <span :class="getButtonLabelClass">
        {{ $t(getPlaceHolder) }}
      </span>

      <!-- Spinner -->
      <div
        class="loading-spinner"
        v-if="!(!spinner || (!dataLoading && spinner))">

        <q-icon
          :allowSelfUpdate="false"
          spin
          :width="getSpinnerSize"
          name="spinner" />

      </div>

      <!-- Right icon -->
      <span
        v-if="iconRight !== null"
        :class="{
          'btn-icon': true,
          'right': true,
          'fa-icon-offset': faIcon
        }">

        <icon
          v-if="faIcon"
          v-bind:style="{color: iconColor}" :name="iconRight" />

        <q-icon
          v-else
          v-bind:style="{color: iconColor}"
          :height="iconHeight"
          :name="iconRight" />

      </span>
    </span>

  </button>
</template>

<script>
import QIcon from '@/components/standard/display/QIcon'
import stylers from '@/mixins/components/standard/input/inputButton/stylers'

const STYLE_PROPS = {
  hollow: 'hollow',
  pointable: 'pointable',
  lighter: 'lighter',
  boxy: 'boxy',
  smallerFont: 'smaller-font',
  active: 'active',
  dissabled: 'disabled',
  inputInlined: 'inputInlined',
  floatRight: 'float-right',
  slimmer: 'slimmer'
}

export default {
  data () {
    return {
      confirmed: false
    }
  },

  methods: {
    /**
     * On click event handler
     */
    click () {
      if (this.dissabled || this.dataLoading) return false

      // If conformation is required and has not yet been confirmed
      if (this.conformation && !this.confirmed) {
        this.confirmed = true
        setTimeout (() => {this.resetState()}, this.getConfirmationTimeout)
      } else {
        // If no conformation or has been confirmed
        this.resetState()
        this.$emit('click', {
          input: this.inputName,
          extra: this.sideValue
        })

        if (!!this.route) {
          this.$router.push(this.route)
        }
      }
    },

    /**
     * Will reset button's state such as conformation status.
     */
    resetState () {
      this.$set(this, 'confirmed', false)
    }
  },

  computed: {
    /**
     * Identifies it self as a button; used by input base
     * @returns {Boolean}
     */
    isButton () {
      return true
    },

    /**
     * Gives confirmation window timeout
     * @returns {Number}
     */
    getConfirmationTimeout () {
      return 5000
    },

    /**
     * Gives spinner's size
     * @returns {String}
     */
    getSpinnerSize () {
      if (this.smaller) return '15px'
      return '33px'
    },

    /**
     * Gives button's label class
     * @returns {String}
     */
    getButtonLabelClass () {
      if (this.spinner && this.dataLoading) return 'hide-from-spinner'
      return ''
    },

    /**
     * Gives extra styles to button
     * @returns {String}
     */
    getExtraInnerStyle () {
      let base = ``
      for (let sp in STYLE_PROPS) {
        if (this.$props[sp]) {
          base += ` ${STYLE_PROPS[sp]}`
        }
      }

      // Preset font size
      base += ` ${this.fontSize}-font-size`

      // Preset button style
      if (this.btnStyle) base += ` ${this.btnStyle}`
      if (this.confirmed) base += ' confirmed-style'

      return base.trim()
    },

    /**
     * Gives button's placeholder label
     * @returns {String}
     */
    getPlaceHolder () {
      if (this.conformation && this.confirmed) return this.confirmText
      return this.getPlaceholderLabel
    }
  },

  components: {
    'q-icon': QIcon
  },

  mixins: [
    stylers
  ],


  props: {
    inputName: {
      type: String,
      required: false
    },
    confirmText: {
      type: String,
      required: false,
      default: 'Button.confirm'
    },
    spinner: {
      type: Boolean,
      required: false,
      default: false
    },
    dataLoading: {
      type: Boolean,
      required: false,
      default: false
    },
    route: {
      type: String,
      required: false,
      default: ''
    },
    conformation: {
      type: Boolean,
      required: false,
      default: false
    },

    // Stylong
    btnStyle: {
      type: String,
      required: false,
      default: null
    },
    faIcon: {
      type: Boolean,
      required: false,
      default: false
    },
    floatRight: {
      type: Boolean,
      required: false,
      default: false
    },
    iconHeight: {
      type: Number,
      required: false,
      default: 12
    },
    inputInlined: {
      type: Boolean,
      required: false,
      default: false
    },
    fontSize: {
      type: String,
      required: false,
      default: 'default'
    },
    iconColor: {
      type: String,
      required: false,
      default: '#7AC143'
    },
    hollow: {
      type: Boolean,
      required: false,
      default: false
    },
    pointable: {
      type: Boolean,
      required: false,
      default: false
    },
    lighter: {
      type: Boolean,
      required: false,
      default: false
    },
    boxy: {
      type: Boolean,
      required: false,
      default: false
    },
    active: {
      type: Boolean,
      required: false,
      default: false
    },
    smallerFont: {
      type: Boolean,
      required: false,
      default: false
    }
  }
}
</script>

<style scoped>
.standard-button {
  padding: 14px 25px;
  border-radius: 40px;
  font-size: 16px;
  border: none;
  background-color: #7AC143;
  color: #FFF;
}
.standard-button .inner-content-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.plain-text {
  border: none;
  color: #353635;
  outline: none;
  background-color: transparent;
}
.pointable {
  cursor: pointer;
}

/* Button styles */
.grayed-out-style {
  background-color: transparent;
  color: #353635;
  border: 1px solid #D2D3D4
}
.activate-campaign-style {
  background-color: #FE2B41;
  color: #FFFFFF;
  border: 1px solid #FE2B41
}
.ok-style {
  background-color: #7AC143;
  border: 1px solid #7AC143;
  color: #FFF;
  transition: all .3s;
}
.nok-style {
  background-color: #FFFFFF;
  color: #353635;
  border: 1px solid #FE2B41;
  transition: all .3s;
}
.remove-style {
  background-color: #F1A5AC;
  border: 1px solid #F1A5AC;
  color: #FFF;
  transition: all .3s;
}
.default-style {
  background-color: #FFFFFF;
  border: 1px solid #FFFFFF;
  color: #353635;
  transition: all .3s;
}
.link-style {
  text-decoration: underline;
  background-color: transparent;
  border-radius: 3px;
  color: #353635;
  transition: all .3s;
}
.fb-style {
  background-color: #4C69BA;
  border: 1px solid #4C69BA;
  border-radius: 3px;
  transition: all .3s;
}
.g-style {
  background-color: #DD4B39;
  border: 1px solid #DD4B39;
  border-radius: 3px;
  transition: all .3s;
}
.logout-style {
  color: #ffffff;
  border: 1px solid #767676;
  border-radius: 14px;
  transition: all .3s;
  color: #7AC143;
}
.confirmed-style {
  background-color: #4C69BA;
  border: 1px solid #4C69BA;
  color: #FFFFFF;
  transition: all .3s;
}

/* Hover effects */
.grayed-out-style:hover {
  background-color: #D2D3D42A;
}
.activate-campaign-style:hover {
  background-color: #FE2B41DF;
}
.ok-style:hover {
  background-color: rgb(100, 146, 64);
  border-color: #7AC143;
}
.nok-style:hover {
  background-color: rgb(255, 218, 221);
  border-color: #FE2B41;
}
.confirmed-style:hover {
  background-color: #4C69BA15;
  border-color: #4C69BA;
  color: #4C69BA;
}
.remove-style:hover {
  background-color: rgb(221, 122, 132);
  border-color: #F1A5AC;
}
.default-style:hover {
  background-color: rgb(207, 207, 207);
  border-color: #FFFFFF;
}
.link-style:hover {
  color: #3B5998;
}
.fb-style:hover {
  background-color: rgb(60, 86, 160);
  border-color: #4C69BA;
}
.g-style:hover {
  background-color: rgb(179, 48, 31);
  border-color: #DD4B39;
}

/* Active effects */
.grayed-out-style:active {
  background-color: transparent;
}
.activate-campaign-style:active {
  background-color: #FE2B41;
}
.ok-style:active {
  background-color: #7AC143;
  border-color: #7AC143;
}
.nok-style:active {
  background-color: rgb(255, 196, 201);
  border-color: #FE2B41;
}
.remove-style:active {
  background-color: #F1A5AC;
  border-color: #F1A5AC;
}
.default-style:active {
  background-color: #FFFFFF;
  border-color: #FFFFFF;
}
.link-style:active {
  /*background-color: #3B5998;*/
}
.fb-style:active {
  background-color: #4C69BA;
  border-color: #4C69BA;
}
.g-style:active {
  background-color: #DD4B39;
  border-color: #DD4B39;
}

/* Hollow styles */
.hollow {
  background-color: transparent;
}
.hollow.ok-style {
  color: #7AC143;
}
.hollow.nok-style {
  color: #FE2B41;
}
.hollow.confirmed-style {
  color: #4C69BA;
}
.hollow.remove-style {
  color: #F1A5AC;
}
.hollow.default-style {
  color: #353635;
  /*color: #353635;
  border-color: #353635;*/
}
.hollow.link-style {
  color: #353635;
}
.hollow.fb-style {
  color: #4C69BA;
}
.hollow.g-style {
  color: #DD4B39;
}
.hollow.logout-style {
  border-color: #767676;
}

/* Hollow hover effects */
.hollow.ok-style:hover {
  background-color: #7AC14315;
  border-color: #7AC143;
}
.hollow.confirmed-style:hover {
  background-color: #4C69BA15;
  border-color: #4C69BA;
}
.hollow.nok-style:hover {
  background-color: #FE2B4115;
  border-color: #FE2B41;
}
.hollow.remove-style:hover {
  background-color: #F1A5AC15;
  border-color: #F1A5AC;
}
.hollow.default-style:hover {
  background-color: #35363515;
  border-color: #FFFFFF;
}
.hollow.link-style:hover {
  color: #3B5998;
}
.hollow.fb-style:hover {
  background-color: #4C69BA15;
  border-color: #4C69BA;
}
.hollow.g-style:hover {
  background-color: #DD4B3915;
  border-color: #DD4B39;
}

/* Hollow active effects */
.hollow.ok-style:active {
  color: #7AC143;
  background-color: transparent;
}
.hollow.nok-style:active {
  color: #FE2B41;
  background-color: transparent;
}
.hollow.remove-style:active {
  color: #F1A5AC;
  background-color: transparent;
}
.hollow.default-style:active {
  color: #353635;
  background-color: transparent;
}
.hollow.link-style:active {
  color: #353635;
  background-color: transparent;
}
.hollow.fb-style:active {
  color: #4C69BA;
  background-color: transparent;
}
.hollow.g-style:active {
  color: #DD4B39;
  background-color: transparent;
}

.smaller {
  padding: 0px 25px;
}

.btn-icon {
  top: -1px;
  position: relative;
  width: 19px;
}
.btn-icon.left {
  right: 14px;
}
.btn-icon.right {
  left: 14px;
}
.smaller-font-size {
  font-size: 11px;
}
.slimmer {
  padding-left: 10px;
  padding-right: 10px;
}
.standard-button.lighter {
  border-color: #D2D3D4;
  color: #D2D3D4;
}

.standard-button.hollow.lighter:hover {
  border-color: #D2D3D4;
}

.boxy {
  border-radius: 3px;
}

.standard-button.smaller-font {
  font-size: 12px;
}

.standard-button.active {
  color: #7AC143;
}

.standard-button.disabled, .standard-button.disabled:hover {
  background-color: #FFFFFF;
  border-color: #353635;
  color: #353635;
  cursor: not-allowed;
}
.standard-button.disabled:hover {
  background-color: #EEEEEE;
}

.fluid {
  width: 100%;
}

.inputInlined {
  padding-top: 10px;
  padding-bottom: 9px;
}

.loading-spinner {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hide-from-spinner {
  color: transparent;
}

.fa-icon-offset {
  top: 2px;
}

</style>




// WEBPACK FOOTER //
// src/components/standard/input/InputButton.vue?21591a63